function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});


const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
* Shopify Common JS
*
*/
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  initDefaultMegaMenu() {
    if (window.matchMedia('(min-width: 991px)').matches) return;

    const firstLi = this.querySelector('li.header-mega-link');
    if (!firstLi) return;

    const details = firstLi.querySelector('details');
    const summary = firstLi.querySelector('summary');
    const resLink = firstLi.querySelector('.res--link');

    const alreadyActive = this.querySelector('li.header-mega-link.is-active');
    if (alreadyActive && alreadyActive !== firstLi) return;

    firstLi.classList.add('is-active');

    resLink?.classList.add('submenu-open');

    details?.setAttribute('open', '');
    details?.classList.add('menu-opening');
    summary?.setAttribute('aria-expanded', 'true');
  }

  connectedCallback() {
    this.mainDetailsToggle?.addEventListener('toggle', () => {
      const isOpen = this.mainDetailsToggle.hasAttribute('open');
      document.body.classList.toggle('overflow-hidden', isOpen);
      document.body.classList.toggle(`overflow-hidden-${this.dataset.breakpoint}`, isOpen);

      if (isOpen) {
        this.initDefaultMegaMenu();
      }
    });
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) => {

      summary.addEventListener(
        'pointerdown',
        (event) => {
          if (window.matchMedia('(min-width: 991px)').matches) {
            event.preventDefault();
            event.stopImmediatePropagation();
          }
        },
        true
      );

      summary.addEventListener(
        'mousedown',
        (event) => {
          if (window.matchMedia('(min-width: 991px)').matches) {
            event.preventDefault();
            event.stopImmediatePropagation();
          }
        },
        true
      );

      summary.addEventListener('click', this.onSummaryClick.bind(this));
    });

    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) =>
      button.addEventListener('click', this.onCloseButtonClick.bind(this))
    );
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {

    if (window.matchMedia('(min-width: 991px)').matches) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const resLink = detailsElement.closest('.res--link');

    const isOpen = detailsElement.hasAttribute('open');

    const activeLi = detailsElement.closest('li.header-mega-link');
    const isActive = activeLi?.classList.contains('is-active');

    // 👉 Close all other open submenu-open
    this.querySelectorAll('.res--link.submenu-open')
      .forEach(el => el !== resLink && el.classList.remove('submenu-open'));

    this.querySelectorAll('li.header-mega-link.is-active')
      .forEach(li => li !== activeLi && li.classList.remove('is-active'));

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen
        ? this.closeMenuDrawer(event, summaryElement)
        : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty(
          '--viewport-height',
          `${window.innerHeight}px`
        );
      }
      return;
    }

    // CLOSE
    if (isOpen && isActive) {
      summaryElement.setAttribute('aria-expanded', 'false');
      resLink?.classList.remove('submenu-open');
      activeLi?.classList.remove('is-active');
      return;
    }

    // OPEN
    setTimeout(() => {
      detailsElement.classList.add('menu-opening');
      summaryElement.setAttribute('aria-expanded', 'true');

      resLink?.classList.add('submenu-open');
      activeLi?.classList.add('is-active');

      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
    }, 100);
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add('overflow-hidden');
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');

    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });

    this.mainDetailsToggle.querySelectorAll('.res--link.submenu-open')
      .forEach(el => el.classList.remove('submenu-open'));

    this.mainDetailsToggle.querySelectorAll('.is-active')
      .forEach(el => el.classList.remove('is-active'));

    document.body.classList.remove('overflow-hidden');
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent)
      elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') &&
        !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const resLink = detailsElement.closest('.res--link');
    const activeLi = detailsElement.closest('li.header-mega-link');

    resLink?.classList.remove('submenu-open');
    activeLi?.classList.remove('is-active');

    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);

    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    detailsElement.removeAttribute('open');

    const parentOpen = detailsElement.closest('details[open]');
    if (parentOpen) {
      trapFocus(
        parentOpen,
        detailsElement.querySelector('summary')
      );
    }
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  resetAllSubmenus() {
    this.querySelectorAll('details').forEach(details => {
      if (details !== this.mainDetailsToggle) {
        details.removeAttribute('open');
        details.classList.remove('menu-opening');
      }
    });

    this.querySelectorAll('.res--link.submenu-open')
      .forEach(el => el.classList.remove('submenu-open'));

    this.querySelectorAll('.is-active')
      .forEach(el => el.classList.remove('is-active'));
  }

  openMenuDrawer(summaryElement) {
    this.resetAllSubmenus();

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
      this.syncShopByDrawerDefaultMobile();
    });

    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add('overflow-hidden');
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  /**
   * Mobile: after menu reset, restore default-open state for every "Shop by" row (does not close/open top-level sections).
   */
  syncShopByDrawerDefaultMobile() {
    if (window.matchMedia('(min-width: 991px)').matches) return;

    this.querySelectorAll('#menu-drawer details[data-shop-by-default]').forEach((shopDetail) => {
      if (!this.mainDetailsToggle?.contains(shopDetail)) return;
      shopDetail.setAttribute('open', '');
      shopDetail.classList.add('menu-opening');
      const sum = shopDetail.querySelector(':scope > summary');
      if (sum) sum.setAttribute('aria-expanded', 'true');
    });
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');

    this.mainDetailsToggle.querySelectorAll('details').forEach(details => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });

    this.mainDetailsToggle.querySelectorAll('.res--link.submenu-open')
      .forEach(el => el.classList.remove('submenu-open'));

    this.mainDetailsToggle.querySelectorAll('.is-active')
      .forEach(li => li.classList.remove('is-active'));

    document.body.classList.remove('overflow-hidden');
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);

    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) {
      elementToFocus?.setAttribute('aria-expanded', false);
    }
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
      (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}



document.addEventListener('DOMContentLoaded', () => {

  // Handle ALL mega menus (important)
  const menus = document.querySelectorAll('.mega-menu__list.page-width');

  if (!menus.length) return;

  menus.forEach(async (menu) => {

    // جلوگیری duplicate run
    if (menu.querySelector('.last-li-with-image')) return;

    // Find standalone li (no nested ul)
    const standaloneLis = Array.from(menu.children).filter(li =>
      li.tagName === 'LI' && !li.querySelector('ul')
    );

    if (!standaloneLis.length) return;

    // Pick last standalone li
    const lastLi = standaloneLis[standaloneLis.length - 1];
    const link = lastLi.querySelector('a');

    const itemName = link ? link.textContent.trim() : 'Item';
    const itemHref = link ? link.href : '#';

    // Remove original li
    lastLi.remove();

    let itemImage = null;

    // Fetch Shopify collection image
    if (itemHref.includes('/collections/')) {
      try {
        const handle = itemHref.split('/collections/')[1].replace('/', '');
        const response = await fetch(`/collections/${handle}.json`);
        const data = await response.json();

        if (data.collection && data.collection.image) {
          itemImage = data.collection.image.src;
        }
      } catch (e) {
        console.warn('Collection image fetch failed', e);
      }
    }

    // ✅ IMPORTANT: use <li> (not div)
    const customItem = document.createElement('li');
    customItem.classList.add('custom-menu-item', 'last-li-with-image');

    customItem.innerHTML = `
      <a href="${itemHref}" class="custom-menu-link">
        ${itemImage ? `<img src="${itemImage}" alt="${itemName}" class="custom-menu-image" />` : ''}
      </a>
    `;

    // Insert at 4th position
    const fourthItem = menu.children[3];

    if (fourthItem) {
      menu.insertBefore(customItem, fourthItem);
    } else {
      menu.appendChild(customItem);
    }

  });

});

document.addEventListener('DOMContentLoaded', () => {

  const getCollectionHandle = (url) => {
    try {
      return new URL(url).pathname.split('/collections/')[1]?.replace(/\/$/, '');
    } catch {
      return null;
    }
  };

  const ResObserver = new MutationObserver(() => {
    const ResMenu = document.querySelector('.menu-drawer__menu.responsive-mega-slide');
    if (!ResMenu) return;

    const ResTopLis = ResMenu.querySelectorAll(':scope > li');

    ResTopLis.forEach(ResLi => {

      // 🚫 Already processed → stop
      if (ResLi.dataset.processed === 'true') return;

      // Skip mega menu items
      if (ResLi.querySelector(':scope > details')) return;

      const ResLink = ResLi.querySelector(':scope > a.menu-drawer__menu-item');
      if (!ResLink) return;

      // 🔒 LOCK immediately
      ResLi.dataset.processed = 'true';

      const ResItemHref = ResLink.href;
      const ResItemName = ResLink.textContent.trim();
      const handle = getCollectionHandle(ResItemHref);
      if (!handle) return;

      fetch(`/collections/${handle}.json`)
        .then(res => res.json())
        .then(data => {
          const image = data.collection?.image?.src;
          if (!image) return;

          const ResCustomDiv = document.createElement('div');
          ResCustomDiv.className = 'custom-menu-content';

          ResCustomDiv.innerHTML = `
              <a href="${ResItemHref}" class="custom-menu-link">
                <img class="w-100 h-100 object-fit-cover" src="${image}" alt="${ResItemName}" class="custom-menu-image" loading="lazy" />
                <span class="tk-elza font26 leading28 text-FBFBF5 text-capitalize">
                  ${ResItemName}
                </span>
              </a>
            `;

          ResLi.appendChild(ResCustomDiv);
        });
    });
  });

  ResObserver.observe(document.body, {
    childList: true,
    subtree: true
  });

});


$(document).ready(function () {
  // Open the first child by default
  $('.faq-item').first().addClass('active').next('.faq-content').slideDown();

  // Handle click events for closet headers
  $('.faq-item').click(function () {
    $(this).toggleClass('active').next('.faq-content').slideToggle();
    $('.faq-item').not(this).removeClass('active').next('.faq-content').slideUp();
  });
});

$(document).ready(function () {
  // Open the first child by default
  $('.right-header-faq').first().addClass('active').next('.right-faq').slideDown();

  // Handle click events for closet headers
  $('.right-header-faq').click(function () {
    $(this).toggleClass('active').next('.right-faq').slideToggle();
    $('.right-header-faq').not(this).removeClass('active').next('.right-faq').slideUp();
  });
});
//end...............


/* ------------------ FACET TOGGLE (MOBILE + DESKTOP) ------------------ */
(function () {
  const STORAGE_KEY = 'facetToggleState';
  const MOBILE_MAX = 990;

  const isMobile = () => window.innerWidth <= MOBILE_MAX;
  const getFacetsContainer = () => document.querySelector('.res-container');

  function applyFacetToggle(isOn) {
    const wrapper = document.getElementById('FacetsWrapperDesktop');
    const grid = document.querySelector('.grid');
    const facetsVertical = document.querySelector('.facets-vertical');
    const container = getFacetsContainer();

    /* ---------- MOBILE ---------- */
    if (isMobile()) {
      if (!container) return;
      container.style.display = isOn ? 'block' : 'none';
      container.classList.toggle('is-open', isOn);
      return;
    }

    /* ---------- DESKTOP (UNCHANGED) ---------- */
    if (!wrapper || !grid || !facetsVertical) return;

    const activeFacets = wrapper.querySelector(
      '.active-facets.active-facets-desktop.custom-changes'
    );

    Array.from(wrapper.children).forEach((child) => {
      if (child === activeFacets) return;
      child.style.display = isOn ? '' : 'none';
    });

    grid.classList.remove('grid--3-col-desktop', 'grid--4-col-desktop');

    if (isOn) {
      grid.classList.add('grid--3-col-desktop');
      facetsVertical.classList.remove('d-block');
      facetsVertical.classList.add('d-flex');
    } else {
      grid.classList.add('grid--4-col-desktop');
      facetsVertical.classList.remove('d-flex');
      facetsVertical.classList.add('d-block');
    }
  }

  window.applyFacetToggle = applyFacetToggle;

  /* ------------------ TOGGLE CHANGE ------------------ */
  document.addEventListener('change', (event) => {
    if (event.target.id !== 'facetToggle') return;

    const isOn = event.target.checked;

    if (!isMobile()) {
      localStorage.setItem(STORAGE_KEY, isOn ? 'on' : 'off');
    }

    applyFacetToggle(isOn);
  });

  /* ------------------ INITIAL LOAD ------------------ */
  document.addEventListener('DOMContentLoaded', () => {
    const toggle = document.getElementById('facetToggle');
    if (!toggle) return;

    /* ---------- MOBILE INIT ---------- */
    if (isMobile()) {
      toggle.checked = false;
      applyFacetToggle(false);

      const productGrid = document.getElementById('ProductGridContainer');
      if (!productGrid) return;

      let forceToggleOff = false;

      /* ---- OBSERVE ALL FILTER CHANGES ---- */
      const observer = new MutationObserver(() => {
        if (!isMobile()) return;

        requestAnimationFrame(() => {
          const t = document.getElementById('facetToggle');
          const container = getFacetsContainer();

          // Facet remove must win AFTER re-render
          if (forceToggleOff) {
            if (t) t.checked = false;
            forceToggleOff = false;
          }

          // Always close container on mobile after filtering
          if (container) {
            container.style.display = 'none';
            container.classList.remove('is-open');
          }
        });
      });

      observer.observe(productGrid, {
        childList: true,
        subtree: true
      });

      /* ---- FACET REMOVE CLICK ---- */
      document.addEventListener('click', (event) => {
        if (!isMobile()) return;

        const facetRemove = event.target.closest('facet-remove');
        if (!facetRemove) return;

        forceToggleOff = true;

        // Immediate UI response
        const t = document.getElementById('facetToggle');
        const container = getFacetsContainer();

        if (t) t.checked = false;
        if (container) {
          container.style.display = 'none';
          container.classList.remove('is-open');
        }
      });

      return;
    }

    /* ---------- DESKTOP INIT (UNCHANGED) ---------- */
    const isOn = localStorage.getItem(STORAGE_KEY) !== 'off';
    toggle.checked = isOn;
    applyFacetToggle(isOn);
  });

  /* ------------------ RESIZE SAFETY (MOBILE ONLY) ------------------ */
  window.addEventListener('resize', () => {
    if (!isMobile()) return;

    const toggle = document.getElementById('facetToggle');
    const container = getFacetsContainer();

    if (toggle) toggle.checked = false;
    if (container) {
      container.style.display = 'none';
      container.classList.remove('is-open');
    }
  });
})();




// Cart
(function () {
  const originalFetch = window.fetch;

  window.fetch = function (...args) {
    return originalFetch.apply(this, args).then(async (response) => {
      const url = args[0];

      if (
        typeof url === 'string' &&
        (url.includes('/cart/change') ||
          url.includes('/cart/update') ||
          url.includes('/cart/add'))
      ) {
        try {
          const cart = await fetch('/cart.js').then(r => r.json());
          const countEl = document.getElementById('cart-page-count');
          if (countEl) {
            countEl.textContent = cart.item_count;
          }
        } catch (e) {
          console.warn('Cart count update failed', e);
        }
      }

      return response;
    });
  };
})();

document.addEventListener('DOMContentLoaded', () => {
  const menu = document.querySelector('.menu-drawer__menu');

  if (!menu) return;

  const toggleElements = () => {
    const isSubmenuOpen = menu.classList.contains('submenu-open');

    document
      .querySelectorAll('.res-header-mobile-number, .header-social-links')
      .forEach(el => {
        el.classList.toggle('d-none', isSubmenuOpen);
      });
  };

  // Initial state check
  toggleElements();

  // Observe class changes
  const observer = new MutationObserver(toggleElements);

  observer.observe(menu, {
    attributes: true,
    attributeFilter: ['class']
  });
});

function getInsertIndex() {
  return window.innerWidth < 750 ? 1 : 5;
}

$(document).ready(function () {
  $('#product-grid > li:nth-child(6)').addClass('custom-position tmb-40');
});

$(document).ready(function () {
  let swapped = false;

  function swapGridItems() {
    const $grid = $('#product-grid');
    const $items = $grid.children('li');

    if ($items.length < 7) return;

    const $sixth = $items.eq(5);
    const $seventh = $items.eq(6);

    if (window.innerWidth <= 992 && !swapped) {
      // Swap positions
      $seventh.insertBefore($sixth);
      // $sixth.removeClass('grid__item').css('width', '100%');
      swapped = true;
    }

    if (window.innerWidth > 992 && swapped) {
      // Restore original order
      $sixth.insertBefore($seventh);
      swapped = false;
    }
  }

  swapGridItems();
  $(window).on('resize', swapGridItems);

  // Shopify AJAX safety
  document.addEventListener('shopify:section:load', swapGridItems);
});

document.addEventListener('DOMContentLoaded', function () {
  const scrollAnchorButtons = document.querySelectorAll('.read-more-btn--scroll-anchor');

  scrollAnchorButtons.forEach((btn) => {
    btn.addEventListener('click', (e) => {
      const targetSelector = btn.getAttribute('href');
      if (!targetSelector || !targetSelector.startsWith('#')) return;

      const target = document.querySelector(targetSelector);
      if (!target) return;

      e.preventDefault();

      const style = window.getComputedStyle(target);
      const rawOffset = style.getPropertyValue('--scroll-offset-top').trim();
      const offset = Number.parseInt(rawOffset || '120', 10);
      const targetTop = target.getBoundingClientRect().top + window.pageYOffset;

      window.scrollTo({
        top: targetTop - (Number.isNaN(offset) ? 120 : offset),
        behavior: 'smooth',
      });
    });
  });
});


document.addEventListener('DOMContentLoaded', function () {

  const updateBodyOverflow = () => {
    const isOpen = document.querySelector('.responsive-header[open]');
    document.body.classList.toggle('overflow-hidden-tablet', !!isOpen);
  };

  // Initial sync
  updateBodyOverflow();

  // Observe attribute changes across the document
  const observer = new MutationObserver(updateBodyOverflow);

  observer.observe(document.documentElement, {
    subtree: true,
    attributes: true,
    attributeFilter: ['open']
  });

});

//slick-slider
function initHeroSlider() {
  var $slider = $(".slider-hero-slider");

  if (!$slider.length) return;

  // Progress bar settings
  let progressInterval;
  let progressTime = 8000;
  let tickRate = 30;

  function startProgressbar() {
    let width = 0;
    const $fill = $(".slider-progressbar-single .progress-fill");

    $fill.css("width", "0%");
    clearInterval(progressInterval);

    progressInterval = setInterval(function () {
      width += (tickRate / progressTime) * 100;
      $fill.css("width", width + "%");

      if (width >= 100) {
        clearInterval(progressInterval);
        $slider.slick("slickNext");
      }
    }, tickRate);
  }

  // Destroy if already initialized
  if ($slider.hasClass("slick-initialized")) {
    $slider.slick("unslick");
  }

  // Init slick with events
  $slider
    .on("init", function () {
      startProgressbar();
    })
    .on("afterChange", function () {
      startProgressbar();
    });

  $slider.slick({
    dots: false,
    infinite: true,
    speed: 300,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: false,
    arrows: false,
    responsive: [
      {
        breakpoint: 991,
        settings: {
          slidesToShow: 1,
        },
      },
      {
        breakpoint: 525,
        settings: {
          slidesToShow: 1,
          infinite: true,
        },
      },
    ],
  });
}


document.addEventListener("DOMContentLoaded", initHeroSlider);
document.addEventListener("shopify:section:load", initHeroSlider);

function marqueeSlider() {
  var $slider = $(".marquee-benefits__wrapper");

  if (!$slider.length) return;

  if ($slider.hasClass("slick-initialized")) {
    $slider.slick("unslick");
  }

  $slider.slick({
    infinite: true,
    speed: 7000,
    autoplay: true,
    autoplaySpeed: 0,
    arrows: false,
    dots: false,
    slidesToShow: 3,
    slidesToScroll: 1,
    cssEase: 'linear',
    pauseOnHover: false,
    pauseOnFocus: false,
    responsive: [
      {
        breakpoint: 991,
        settings: {
          slidesToShow: 1,
        },
      },
      {
        breakpoint: 525,
        settings: {
          slidesToShow: 1,
          infinite: false,
        },
      },
    ],
  });
}

document.addEventListener("DOMContentLoaded", marqueeSlider);
document.addEventListener("shopify:section:load", marqueeSlider);

function collectionSlider() {
  var $slider = $(".collection-selector__list");

  if (!$slider.length) return;

  if ($slider.hasClass("slick-initialized")) {
    $slider.slick("unslick");
  }

  $slider.slick({
    dots: false,
    infinite: false,
    speed: 300,
    slidesToShow: 4,
    slidesToScroll: 1,
    autoplay: false,
    arrows: false,
    responsive: [
      {
        breakpoint: 991,
        settings: {
          slidesToShow: 1,
        },
      },
      {
        breakpoint: 525,
        settings: {
          slidesToShow: 1,
          infinite: false,
        },
      },
    ],
  });
}

document.addEventListener("DOMContentLoaded", collectionSlider);
document.addEventListener("shopify:section:load", collectionSlider);

function testimonialSlider() {
  var $slider = $(".reviews-cards__grid");

  if (!$slider.length) return;

  if ($slider.hasClass("slick-initialized")) {
    $slider.slick("unslick");
  }

  $slider.slick({
    dots: false,
    infinite: true,
    speed: 300,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: false,
    arrows: false,
    responsive: [
      {
        breakpoint: 991,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 525,
        settings: {
          slidesToShow: 1,
          infinite: false,
        },
      },
    ],
  });
}

document.addEventListener("DOMContentLoaded", testimonialSlider);
document.addEventListener("shopify:section:load", testimonialSlider);

function LogoSlider() {
  var $slider = $(".logo-list");

  if (!$slider.length) return;

  if ($slider.hasClass("slick-initialized")) {
    $slider.slick("unslick");
  }

  $slider.slick({
    infinite: true,
    speed: 7000,
    autoplay: true,
    autoplaySpeed: 0,
    arrows: false,
    dots: false,
    slidesToShow: 5,
    slidesToScroll: 1,
    cssEase: 'linear',
    pauseOnHover: false,
    pauseOnFocus: false,
    responsive: [
      {
        breakpoint: 991,
        settings: {
          slidesToShow: 3,
        },
      },
      {
        breakpoint: 525,
        settings: {
          slidesToShow: 2,
          infinite: false,
        },
      },
    ],
  });
}

document.addEventListener("DOMContentLoaded", LogoSlider);
document.addEventListener("shopify:section:load", LogoSlider);

function teamSlider() {
  var $slider = $(".res-team-card-slider");

  if (!$slider.length) return;

  if ($slider.hasClass("slick-initialized")) {
    $slider.slick("unslick");
  }

  $slider.slick({
    dots: false,
    infinite: true,
    speed: 300,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: false,
    arrows: false,
    responsive: [
      {
        breakpoint: 991,
        settings: {
          slidesToShow: 1,
        },
      },
      {
        breakpoint: 525,
        settings: {
          slidesToShow: 1,
          infinite: false,
        },
      },
    ],
  });
}

document.addEventListener("DOMContentLoaded", teamSlider);
document.addEventListener("shopify:section:load", teamSlider);

function productSlider() {
  var $slider = $(".related-product-slider");

  if (!$slider.length) return;

  if ($slider.hasClass("slick-initialized")) {
    $slider.slick("unslick");
  }

  $slider.slick({
    dots: false,
    infinite: false,
    speed: 300,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: false,
    arrows: false,
    responsive: [
      {
        breakpoint: 991,
        settings: {
          slidesToShow: 1,
        },
      },
      {
        breakpoint: 525,
        settings: {
          slidesToShow: 1,
          infinite: false,
        },
      },
    ],
  });
}

document.addEventListener("DOMContentLoaded", productSlider);
document.addEventListener("shopify:section:load", productSlider);


/* ===============================
ADD SUGGESTED PRODUCT (AJAX)
================================ */
document.addEventListener('click', function (e) {
  const btn = e.target.closest('.js-add-suggested');
  if (!btn) return;

  e.preventDefault();

  // Hide button and show loader
  btn.style.display = 'none';

  // Create loader element if it doesn't exist
  let loader = btn.nextElementSibling;
  if (!loader || !loader.classList.contains('suggested-loader')) {
    loader = document.createElement('div');
    loader.className = 'suggested-loader';
    loader.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66">
          <circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"></circle>
        </svg>
      `;
    btn.parentNode.insertBefore(loader, btn.nextSibling);
  }
  loader.style.display = 'inline-block';

  fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      id: btn.dataset.variantId,
      quantity: 1
    })
  })
    .then(async () => {
      await refreshCartDrawer();
      updateCartCountBubble(); // update header bubble
    })
    .finally(() => {
      // Restore button and hide loader
      btn.style.display = '';
      loader.style.display = 'none';
    });
});

// Update cart count bubble
function updateCartCountBubble() {
  const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble span[aria-hidden="true"]');
  if (!bubble) return;

  fetch('/cart.js')
    .then(res => res.json())
    .then(cart => {
      bubble.textContent = cart.item_count;
      // optional: hide bubble if empty
      bubble.closest('.cart-count-bubble').style.display = cart.item_count > 0 ? 'block' : 'none';
    })
    .catch(err => console.error('Failed to update cart bubble', err));
}

// Refresh cart drawer (your existing function)
async function refreshCartDrawer() {
  const response = await fetch('/?section_id=cart-drawer');
  const html = await response.text();

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const newDrawer = doc.querySelector('#CartDrawer');
  const currentDrawer = document.querySelector('#CartDrawer');

  if (newDrawer && currentDrawer) {
    currentDrawer.innerHTML = newDrawer.innerHTML;
  }

  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer?.open) {
    cartDrawer.open();
  }
}

/* ===============================
  REFRESH CART DRAWER + SUGGESTIONS
================================ */
function refreshCartDrawer() {
  fetch('/?section_id=cart-drawer')
    .then(response => response.text())
    .then(html => {
      const dom = new DOMParser().parseFromString(html, 'text/html');

      const newDrawer = dom.querySelector('cart-drawer');
      const currentDrawer = document.querySelector('cart-drawer');

      if (newDrawer && currentDrawer) {
        currentDrawer.innerHTML = newDrawer.innerHTML;
      }
    });
}

/* ===============================
  AUTO REFRESH AFTER REMOVE / UPDATE
================================ */
document.addEventListener('change', function (e) {
  if (
    e.target.matches('input[name="updates[]"]') ||
    e.target.closest('cart-remove-button')
  ) {
    setTimeout(refreshCartDrawer, 300);
  }
});


// document.addEventListener("DOMContentLoaded", function () {
//     const el = document.getElementById("CartDrawer-Suggestions");
//     const el2 = document.getElementsByClassName("custom-suggestions-sidebar")[0];

//     if (
//         el &&
//         el2 &&
//         el.textContent.trim() === "" &&
//         el.children.length === 0
//     ) {
//         el2.style.display = "none";
//     }
//   });
class GalleryDots extends HTMLElement {
  connectedCallback() {
    if (this.slider) return;
    this.slider = document.getElementById(this.dataset.slider);
    if (!this.slider) return;

    this.slider.addEventListener('scroll', this.update.bind(this), { passive: true });
    this.addEventListener('click', this.onDotClick.bind(this));
    new MutationObserver(this.render.bind(this)).observe(this.slider, { childList: true });
    this.render();
  }

  get slides() {
    return Array.from(this.slider.querySelectorAll('.slider__slide'));
  }

  get dots() {
    return Array.from(this.querySelectorAll('.slider-dot'));
  }

  render() {
    const slideCount = this.slides.length;
    const dots = this.dots;

    dots.slice(slideCount).forEach((dot) => dot.remove());
    for (let i = dots.length; i < slideCount; i++) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'slider-dot';
      dot.setAttribute('aria-controls', this.dataset.slider);
      dot.setAttribute('aria-label', this.dataset.dotLabel.replace('[index]', i + 1));
      this.append(dot);
    }

    this.classList.toggle('small-hide', slideCount < 2);
    this.update();
  }

  update() {
    const slides = this.slides;
    if (slides.length < 2) return;

    const slideOffset = slides[1].offsetLeft - slides[0].offsetLeft;
    if (!slideOffset) return;

    const activeIndex = Math.round(this.slider.scrollLeft / slideOffset);
    this.dots.forEach((dot, index) => {
      if (index === activeIndex) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  onDotClick(event) {
    const dot = event.target.closest('.slider-dot');
    if (!dot) return;

    const slides = this.slides;
    const slide = slides[this.dots.indexOf(dot)];
    if (!slide) return;

    this.slider.scrollTo({ left: slide.offsetLeft - slides[0].offsetLeft, behavior: 'smooth' });
  }
}

customElements.define('gallery-dots', GalleryDots);





document.addEventListener('DOMContentLoaded', () => {
  const detailsEl = document.querySelector('header details-modal details');

  if (!detailsEl) return;

  const mediaQuery = window.matchMedia('(max-width: 991px)');

  const handleDetailsState = () => {
    if (mediaQuery.matches) {

      detailsEl.setAttribute('open', '');
    } else {

      detailsEl.removeAttribute('open');
    }
  };


  handleDetailsState();

  mediaQuery.addEventListener('change', handleDetailsState);
});

function moveCartElements() {
  if (!$('body').hasClass('template-cart')) return;

  if ($(window).width() <= 1024) {
    // Mobile / Tablet
    $('#main-cart-footer').insertAfter('.cart-form-wrapper');
  } else {
    // Desktop (your original logic)
    $('#main-cart-footer').insertAfter('.cart-content');
    $('.product-section').insertAfter('.cart-form-wrapper');
  }
}

$(document).ready(moveCartElements);
$(window).on('resize', moveCartElements);

(function () {
  function stripGBP(root = document) {
    root.querySelectorAll('.boost-sd__format-currency').forEach(el => {
      if (el.textContent.includes('GBP')) {
        el.textContent = el.textContent.replace(/\s*GBP/g, '').trim();
      }
    });
  }

  // Run once on load
  document.addEventListener('DOMContentLoaded', () => {
    stripGBP();
  });

  // Watch for Boost re-rendering prices
  const observer = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      mutation.addedNodes.forEach(node => {
        if (node.nodeType === 1) {
          stripGBP(node);
        }
      });
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
})();



(function () {
  function injectBoostHoverImages() {
    document.querySelectorAll('.boost-sd__product-item').forEach(function (item) {
      if (item.querySelector('.boost-sd__product-image-img--hover')) return;

      const productData = item.getAttribute('data-product');
      if (!productData) return;

      let data;
      try {
        data = JSON.parse(productData);
      } catch (e) {
        return;
      }

      if (!data.images || data.images.length < 2) return;

      const mainImg = item.querySelector('.boost-sd__product-image-img--main');
      if (!mainImg) return;

      const hoverImg = document.createElement('img');
      hoverImg.src = data.images[1].src + '&width=500';
      hoverImg.alt = mainImg.alt || '';
      hoverImg.loading = 'lazy';
      hoverImg.className =
        'boost-sd__product-image-img boost-sd__product-image-img--hover';

      mainImg.parentNode.appendChild(hoverImg);
    });
  }

  // Initial load
  document.addEventListener('DOMContentLoaded', injectBoostHoverImages);

  // Re-run for Boost AJAX / Slick updates
  const observer = new MutationObserver(injectBoostHoverImages);
  observer.observe(document.body, { childList: true, subtree: true });
})();

  

document.querySelectorAll('.product__media-item').forEach(item => {
  const img = item.querySelector('img');

  if (!img) return;

  function checkOrientation() {
    if (img.naturalHeight > img.naturalWidth) {
      item.classList.add('portrait');
      item.classList.remove('landscape');
    } else {
      item.classList.add('landscape');
      item.classList.remove('portrait');
    }
  }

  if (img.complete) {
    checkOrientation();
  } else {
    img.onload = checkOrientation;
  }
});

function updateQtyClasses() {
  const btn = document.querySelector('[id^="ProductSubmitButton"]');
  const qtyInput = document.querySelector('.custom--quantity--content quantity-input');

  if (!btn) return;

  const span = btn.querySelector('span');
  if (!span) return;

  const text = span.textContent.trim().toLowerCase();

  // Remove old classes
  btn.classList.remove('sold-out-qty', 'availability-qty');
  if (qtyInput) {
    qtyInput.classList.remove('sold-out-qty', 'availability-qty');
  }

  // Decide class
  const newClass = text.includes('sold out') ? 'sold-out-qty' : 'availability-qty';

  // Add class to both
  btn.classList.add(newClass);
  if (qtyInput) {
    qtyInput.classList.add(newClass);
  }
}

// Init + observer
document.addEventListener('DOMContentLoaded', function () {
  updateQtyClasses();

  const btn = document.querySelector('[id^="ProductSubmitButton"]');
  if (!btn) return;

  const span = btn.querySelector('span');
  if (!span) return;

  // 👇 Observe text changes
  const observer = new MutationObserver(() => {
    updateQtyClasses();
  });

  observer.observe(span, {
    childList: true,
    subtree: true,
    characterData: true
  });
});